/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SEARCH_MOBILE_LAYOUT
 * Date:         2026-02-10
 * Modified By:  Claude
 *
 * Description:
 * Mobile layout for Search page. Uses FilterDrawer pattern, reuses
 * MobileBroadcastList (Variant 3: footer with company only) and MobileInventoryCard.
 *
 * File Size Check:
 * - Current: NEW FILE
 * - Projected: ~190 lines
 * - Decision: Proceed (within 200 line limit)
 *
 * Related Files:
 * - client/src/components/search/Search.tsx
 * - client/src/features/dashboard/components/MobileBroadcastList.tsx
 * - client/src/features/inventory/components/MobileInventoryCard.tsx
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inventory } from "../../interfaces/Inventory";
import { User } from "../../interfaces/User";
import { FlattenedBroadcastItem } from "../broadcast/BroadcastItemCard";
import { FilterDrawer } from "../ui/FilterDrawer";
import { PaginationControls } from "../ui/PaginationControls";
import { MobileBroadcastList } from "../../features/dashboard/components/MobileBroadcastList";
import { MobileInventoryCard } from "../../features/inventory/components/MobileInventoryCard";
import { CompanyCard } from "../companies/CompanyCard";
import { mapSearchBroadcastToFlatItem } from "./utils/broadcastHelpers";
import { TabType } from "./types";
import styles from "./Search.module.scss";

interface CurrentTabData {
  items: any[];
  total: number;
  pages: number;
  page: number;
  loading: boolean;
  error: string | null;
  itemsPerPage: number;
}

interface MobileSearchLayoutProps {
  state: any;
  currentTabData: CurrentTabData;
  handleTabChange: (tab: TabType) => void;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (limit: number) => void;
  handleClearFilters: () => void;
  handleRetry: () => void;
  getSortDisplayValue: () => string;
  handleSortChange: (value: string) => void;
  onInventoryClick: (item: Inventory) => void;
  onBroadcastClick: (item: FlattenedBroadcastItem) => void;
  onCompanyClick: (company: User) => void;
  filterContent: React.ReactNode;
  activeFilterCount: number;
}

export const MobileSearchLayout: React.FC<MobileSearchLayoutProps> = ({
  state,
  currentTabData,
  handleTabChange,
  handlePageChange,
  handleItemsPerPageChange,
  handleClearFilters,
  handleRetry,
  getSortDisplayValue,
  handleSortChange,
  onInventoryClick,
  onBroadcastClick,
  onCompanyClick,
  filterContent,
  activeFilterCount,
}) => {
  const navigate = useNavigate();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Loading state
  if (currentTabData.loading) {
    return (
      <div className={styles.mobileContainer}>
        <div className="text-center py-5">Loading...</div>
      </div>
    );
  }

  // Error state
  if (currentTabData.error) {
    return (
      <div className={styles.mobileContainer}>
        <div className="text-center py-5 text-danger">
          {currentTabData.error}
          <button className="btn btn-link" onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mobileContainer}>
      {/* Toolbar: Sort + Filter */}
      <div className={styles.toolbar}>
        {(state.activeTab === "inventory" || state.activeTab === "broadcasts") && (
          <div className={styles.toolbarSort}>
            <span>Sort:</span>
            <select
              value={getSortDisplayValue()}
              onChange={(e) => handleSortChange(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              {state.activeTab === "inventory" && (
                <>
                  <option value="price_desc">Price: High-Low</option>
                  <option value="price_asc">Price: Low-High</option>
                </>
              )}
            </select>
          </div>
        )}
        <button
          className={`filter-button ${activeFilterCount > 0 ? "has-filters" : ""}`}
          onClick={() => setFilterDrawerOpen(true)}
        >
          <svg className="filter-button__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="filter-button__count">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filters"
        activeFilterCount={activeFilterCount}
        onClear={handleClearFilters}
        onApply={() => setFilterDrawerOpen(false)}
      >
        {filterContent}
      </FilterDrawer>

      {/* Results Header */}
      <div className={styles.resultsHeader}>
        {state.q ? (
          <span>Search Results for <strong className={styles.queryHighlight}>"{state.q}"</strong></span>
        ) : (
          <span>Search Results</span>
        )}
      </div>

      {/* Tab Bar */}
      <div className={styles.tabBar}>
        {(["broadcasts", "inventory", "companies"] as TabType[]).map((tab) => (
          <button
            key={tab}
            className={`${styles.tabItem} ${state.activeTab === tab ? styles.tabItemActive : ""}`}
            onClick={() => handleTabChange(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {state.activeTab === "broadcasts" && (
        <>
          {currentTabData.items.length === 0 ? (
            <div className="text-center py-4 text-muted">
              {state.q ? "No broadcast results found" : "Enter a search query to find broadcasts"}
            </div>
          ) : (
            <MobileBroadcastList
              items={(currentTabData.items as FlattenedBroadcastItem[]).map(mapSearchBroadcastToFlatItem)}
              showFooter={true}
              showActions={false}
              showTypeColumn={true}
              onItemClick={(flatItem) => {
                navigate(`/allbroadcasts/item/${flatItem.broadcastId}/${flatItem.itemNo}`);
              }}
            />
          )}
        </>
      )}

      {state.activeTab === "inventory" && (
        <>
          {currentTabData.items.length === 0 ? (
            <div className="text-center py-4 text-muted">
              {state.q ? "No inventory results found" : "Enter a search query to find inventory"}
            </div>
          ) : (
            <div className="mobile-list-container">
              {(currentTabData.items as Inventory[]).map((item) => (
                <MobileInventoryCard
                  key={item._id}
                  item={item}
                  onClick={onInventoryClick}
                  showSellerInfo={true}
                />
              ))}
            </div>
          )}
        </>
      )}

      {state.activeTab === "companies" && (
        <>
          {currentTabData.items.length === 0 ? (
            <div className="text-center py-4 text-muted">
              {state.q ? "No companies found" : "Enter a search query to find companies"}
            </div>
          ) : (
            <div className="mobile-list-container">
              {(currentTabData.items as User[]).map((company) => (
                <CompanyCard key={company._id} company={company} onClick={onCompanyClick} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {currentTabData.items.length > 0 && (
        <PaginationControls
          currentPage={currentTabData.page}
          totalPages={currentTabData.pages}
          itemsPerPage={currentTabData.itemsPerPage}
          totalItems={currentTabData.total}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          loading={currentTabData.loading}
        />
      )}
    </div>
  );
};

export default MobileSearchLayout;
